<template>
  <div class="h-select">
    <WLabel v-if="label">{{ label }}</WLabel>
    <div class="h-select__select">
      <div
        ref="select"
        class="h-select__select-container"
        :class="{ 'no-border': noBorder }"
        :data-disabled="shouldDisabled"
        tabindex="0"
        @click.prevent.stop="toggleOptions"
        @focusout.prevent.stop="handleFocusout"
      >
        <div v-if="hasIconLeftSlot" class="h-select__icon">
          <slot name="iconLeft"></slot>
        </div>
        <div class="h-select__input-container">
          <template v-if="multiple && value">
            <SelectTag
              v-for="option in value"
              :key="getKey(option)"
              :label="getLabel(option)"
              @remove="removeOption(option)"
            />
          </template>
          <input
            :id="inputId"
            ref="input"
            autocomplete="off"
            :name="inputId"
            :aria-label="inputId"
            :readonly="readonly"
            :value="inputValue"
            :required="required"
            :disabled="shouldDisabled"
            :maxlength="maxlength"
            :placeholder="placeholder"
            @input="handleInput"
            @keyup.enter.prevent="handleEnter"
            @submit.enter.prevent
            @keydown.enter.prevent
            @keydown="handleKeyDown"
          />
        </div>

        <span v-if="maxlength" class="h-select__counter">
          {{ counter }}
        </span>

        <div v-else class="h-select__icon">
          <slot name="iconRight">
            <div class="icon__chevron" :class="{ up: showOptions }">
              <WIcon icon="icChevronArrowDown" size="medium" />
            </div>
          </slot>
        </div>
      </div>
      <div
        v-show="showOptions && options.length"
        ref="options"
        class="h-select__options"
        data-testid="select-options"
        :style="optionsPosition"
      >
        <div
          v-for="(option, index) in filteredOptions"
          :key="getKey(option)"
          class="h-select__option"
          @mousedown="handleClick(option)"
        >
          <slot
            v-if="hasOptionsSlot"
            name="options"
            :option="option || {}"
            :index="index"
          ></slot>
          <div v-else>
            {{ getLabel(option) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { WIcon, WLabel } from '@alice-health/wonderland-vue'
import { nextTick } from 'vue'

import { isComponentOutOfScren } from '@/utils/fixedPosition'

import SelectTag from './SelectTag.vue'
export default {
  components: { WIcon, WLabel, SelectTag },
  props: {
    id: {
      type: String,
      default: undefined
    },
    label: {
      type: String,
      default: ''
    },
    value: { type: [String, Number, Object, Array], default: undefined },
    options: {
      type: Array,
      default: () => []
    },
    optionLabel: {
      type: String,
      default: undefined
    },
    optionKey: {
      type: String,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: undefined
    },
    required: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: ''
    },
    noBorder: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean
    }
  },
  emits: ['input', 'focusout', 'keyDown', 'enter', 'toggle', 'update:value'],
  data() {
    return {
      inputId: this.id || this._.uid,
      showOptions: false,
      inputValue: null,
      optionsTop: 0,
      optionsLeft: 0,
      filteredOptions: []
    }
  },
  computed: {
    hasOptionsSlot() {
      return !!this.$slots.options
    },
    hasIconRightSlot() {
      return !!this.$slots.iconRight
    },
    hasIconLeftSlot() {
      return !!this.$slots.iconLeft
    },
    counter() {
      if (!this.maxlength) {
        return 0
      }
      return this.maxlength - (this.inputValue?.length || 0)
    },
    optionsPosition() {
      return {
        top: this.optionsTop + 'px',
        left: this.optionsLeft + 'px'
      }
    },
    shouldDisabled() {
      return this.disabled || null
    }
  },
  watch: {
    value(value) {
      this.inputValue = this.getLabel(value)
    },
    options() {
      this.showOptionsPosition()
      this.filteredOptions = this.options
    }
  },
  created() {
    this.inputValue = this.getLabel(this.value)
    this.filteredOptions = this.options
  },
  methods: {
    selectValue(value) {
      if (this.multiple) {
        return this.value ? [...this.value, value] : [value]
      }
      return value
    },
    removeOption(option) {
      const value = this.value.filter(
        value => this.getKey(value) !== this.getKey(option)
      )
      this.emitValue(value)
    },
    emitValue(value) {
      this.$emit('update:value', value)
    },
    getKey(option) {
      if (this.optionKey) {
        return option && option[this.optionKey]
      }
      return option
    },
    getLabel(option) {
      if (!option) {
        return ''
      }
      if (this.optionLabel) {
        return option[this.optionLabel]
      }
      return option
    },
    handleFocusout(event) {
      if (
        event.target === this.$refs.select ||
        event.relatedTarget === this.$refs.options
      ) {
        this.$refs.input?.focus()
        return
      }
      this.closeOptions()
      if (!this.value) {
        this.inputValue = null
      }

      this.$emit('focusout', event)
      this.filteredOptions = this.options
    },
    handleInput(event) {
      this.inputValue = event.target.value
      this.showOptionsPosition()
      this.showOptions = true
      this.$emit('input', event.target.value)

      this.filterOptions()
    },
    filterOptions() {
      if (!this.inputValue) {
        this.filteredOptions = this.options
        return
      }
      this.filteredOptions = this.options.filter(option =>
        this.getLabel(option)
          .toLowerCase()
          .includes(this.inputValue?.toLowerCase())
      )
    },
    handleClick(option) {
      this.emitValue(this.selectValue(option))
      this.showOptions = false
    },
    handleEnter() {
      this.$emit('enter', this.inputValue)
      this.closeOptions()
    },
    handleKeyDown(event) {
      this.$emit('keyDown', event)
    },
    toggleOptions() {
      if (this.disabled) {
        return
      }
      this.showOptions = !this.showOptions
      if (this.showOptions) {
        this.$refs.input?.focus()
        this.showOptionsPosition()
      }
      this.$emit('toggle', this.showOptions)
    },
    closeOptions() {
      this.showOptions = false
      if (!this.value) {
        this.inputValue = null
      }
      this.$emit('toggle', false)
    },
    async showOptionsPosition() {
      this.setOptionsInitialPosition()
      await nextTick()
      const position = isComponentOutOfScren(this.$refs.options)
      const rect = this.$refs.options?.getBoundingClientRect()
      if (position.top) {
        this.optionsTop = -rect.height - 5
      }
      if (position.left) {
        this.optionsLeft = -(rect.x + rect.width - window.innerWidth)
      }
    },
    setOptionsInitialPosition() {
      const rect = this.$refs.select?.getBoundingClientRect()
      this.optionsTop = rect.height
    }
  }
}
</script>
<style scoped lang="scss">
.h-select {
  width: 100%;

  &__select {
    position: relative;
  }

  input {
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
    flex: 1;
    color: var(--gl-color-shades-gray-100);
    border-radius: var(--gl-border-radius-sm);
    display: block;
    caret-color: var(--gl-color-shades-magenta-50);
    line-height: var(--gl-line-height-xxs);
    font-size: var(--gl-font-size-xxs);
    font-weight: var(--gl-font-weight-light);
    padding: var(--gl-spacing-02) var(--gl-spacing-03);
    resize: none;
    font-family: var(--gl-font-family-base);
  }

  input[readonly] {
    background-image: none;
    padding-right: 0;
    padding-left: 0;
  }
  &__select-container {
    position: relative;
    border: var(--gl-border-width-xs) solid #dadada;
    border-radius: var(--gl-spacing-02);
    background-color: var(--gl-color-shades-white);
    padding-left: var(--gl-spacing-03);
    &:focus-within {
      border-color: #e10096;
    }
    &:hover {
      border-color: #000000;
    }
    display: flex;
    align-items: center;

    &.no-border {
      border: 0;
      cursor: pointer;
      input {
        cursor: pointer;
      }
      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
    &[data-disabled] {
      background-color: #fafafa;
      cursor: not-allowed;
    }
  }
  &__input-container {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: var(--gl-spacing-01);
    flex: 1 1 auto;
  }
  &__icon {
    display: flex;
    align-items: center;
    margin-right: var(--gl-spacing-03);
    fill: var(--gl-color-shades-gray-100);

    .icon__chevron {
      transition: transform 0.5s ease;
      &.up {
        transform: rotateZ(180deg);
      }
    }
  }

  &__counter {
    color: var(--gl-color-shades-gray-40);
    margin: 0 var(--gl-spacing-03);
    font-size: var(--gl-font-size-xxxs);
  }
  &__options {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    border-radius: var(--gl-border-radius-sm);
    background-color: var(--gl-color-shades-white);
    border: var(--gl-border-width-xs) solid #dadada;
    padding: var(--gl-spacing-02) 0;
    list-style: none;
    margin: var(--gl-spacing-01) 0 0;
    max-height: 210px;
    overflow-y: auto;
    z-index: 21;
    cursor: pointer;
    min-width: 350px;
    width: 100%;
  }
  &__option {
    padding: var(--gl-spacing-03);
    &:hover {
      color: #e10096;
    }
  }
}
</style>
